import React, { useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import {
  FaCalendarAlt,
  FaUser,
  FaMinus,
  FaPlus,
  FaCaretDown,
  FaCaretUp,
} from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../css/SearchSection.css";
import { useTranslation } from "react-i18next";

const SearchSection = ({ onSearch }) => {
  const { i18n } = useTranslation();
  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const handleRangeChange = (value) => {
    if (Array.isArray(value) && value.length === 2) {
      setStartDate(value[0]);
      setEndDate(value[1]);
      setShowCalendar(false);
    }
  };

  const handleSearch = async () => {
    if (!endDate) {
      alert(
        i18n.language === "EN"
          ? "Please select check-out date."
          : "Silakan pilih tanggal check-out."
      );
      return;
    }

    if (typeof onSearch !== "function") return;

    setSubmitting(true);
    try {
      const payload = {
        check_in_date: startDate.toISOString().split("T")[0],
        check_out_date: endDate.toISOString().split("T")[0],
        rooms: [
          {
            room_type_id: null,
            number_of_rooms: rooms,
            guests: adults + children,
          },
        ],
      };

      await onSearch(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      id="search-section"
      className="position-relative search-container"
      style={{ marginTop: "-80px" }}
    >
      <div className="row align-items-center justify-content-center bg-white p-4 shadow rounded position-relative z-1">
        <div className="col-12 col-md-4 position-relative search-box">
          <label className="fw-bold mb-1 d-block">
            {i18n.language === "EN"
              ? "Check-in / Check-out"
              : "Tanggal Menginap"}
          </label>

          <div
            className="d-flex align-items-center border p-3 rounded cursor-pointer"
            onClick={() => {
              setShowCalendar((s) => !s);
              setShowGuests(false);
            }}
            role="button"
            tabIndex={0}
          >
            <FaCalendarAlt className="text-success me-2 fs-4" />
            <Form.Control
              type="text"
              readOnly
              className="border-0 bg-white"
              value={
                endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : startDate.toLocaleDateString()
              }
            />
          </div>

          {showCalendar && (
            <div
              className="calendar-dropdown shadow-sm p-2 bg-white rounded"
              style={{ position: "absolute", zIndex: 100 }}
            >
              <Calendar
                selectRange
                onChange={handleRangeChange}
                value={[startDate, endDate]}
                minDate={today}
              />
            </div>
          )}
        </div>
        <div className="col-12 col-md-4 position-relative search-box">
          <label className="fw-bold mb-1 d-block">
            {i18n.language === "EN" ? "Guests" : "Tamu"}
          </label>
          <div
            className="d-flex align-items-center border p-3 rounded cursor-pointer"
            onClick={() => {
              setShowGuests((s) => !s);
              setShowCalendar(false);
            }}
            role="button"
            tabIndex={0}
          >
            <FaUser className="text-success me-2 fs-4" />
            <Form.Control
              type="text"
              readOnly
              className="border-0 bg-white"
              value={
                i18n.language === "EN"
                  ? `${adults} Adults - ${children} Children - ${rooms} Rooms`
                  : `${adults} Dewasa - ${children} Anak - ${rooms} Kamar`
              }
            />
            {showGuests ? (
              <FaCaretUp className="ms-auto text-success" />
            ) : (
              <FaCaretDown className="ms-auto text-success" />
            )}
          </div>

          {showGuests && (
            <div
              className="guests-dropdown p-3 shadow bg-white rounded"
              style={{ position: "absolute", zIndex: 100 }}
            >
              {[
                {
                  key: "Adults",
                  labelEn: "Adults",
                  labelId: "Dewasa",
                  count: adults,
                  setCount: setAdults,
                  min: 1,
                },
                {
                  key: "Children",
                  labelEn: "Children",
                  labelId: "Anak",
                  count: children,
                  setCount: setChildren,
                  min: 0,
                },
                {
                  key: "Rooms",
                  labelEn: "Rooms",
                  labelId: "Kamar",
                  count: rooms,
                  setCount: setRooms,
                  min: 1,
                },
              ].map(({ key, labelEn, labelId, count, setCount, min }) => (
                <div
                  key={key}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span className="fw-bold">
                    {i18n.language === "EN" ? labelEn : labelId}
                  </span>
                  <div className="d-flex align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => count > min && setCount(count - 1)}
                      disabled={count <= min}
                    >
                      <FaMinus />
                    </Button>
                    <span className="mx-2">{count}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCount(count + 1)}
                    >
                      <FaPlus />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-12 col-md-4 text-center search-box-btn">
          <Button
            variant="success"
            className="w-100 p-3 mt-3"
            onClick={handleSearch}
            disabled={submitting || !endDate}
          >
            {submitting
              ? i18n.language === "EN"
                ? "Searching..."
                : "Mencari..."
              : i18n.language === "EN"
              ? "Search"
              : "Cari"}
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default SearchSection;
